import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { rbacService, RoleDefinition, RBACPermission } from '@/services/rbac';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { UserRole, NavigationModule, PermissionType } from '@/types/enums';

export const RoleEditPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { role } = useParams<{ role: string }>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [roleData, setRoleData] = useState<RoleDefinition | null>(null);
  const [permissions, setPermissions] = useState<RBACPermission[]>([]);

  useEffect(() => {
    if (role) {
      fetchRole();
    }
  }, [role]);

  const fetchRole = async () => {
    try {
      setLoading(true);
      const roleDef = await rbacService.getRole(role as UserRole);
      setRoleData(roleDef);
      setPermissions(roleDef?.permissions || []);
    } catch (error) {
      console.error('Error fetching role:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!roleData || !role) return;

    try {
      setSaving(true);
      await rbacService.updateRolePermissions(role as UserRole, permissions);
      navigate('/rbac/roles');
    } catch (error) {
      console.error('Error updating role:', error);
    } finally {
      setSaving(false);
    }
  };

  const togglePermission = (module: NavigationModule, permission: PermissionType) => {
    setPermissions(prev => {
      const modulePermission = prev.find(p => p.module === module);
      
      if (modulePermission) {
        const hasPermission = modulePermission.permissions.includes(permission);
        const updatedPermissions = hasPermission
          ? modulePermission.permissions.filter(p => p !== permission)
          : [...modulePermission.permissions, permission];

        return prev.map(p => 
          p.module === module 
            ? { ...p, permissions: updatedPermissions }
            : p
        );
      } else {
        // Add new module permission
        return [...prev, { module, permissions: [permission] }];
      }
    });
  };

  const hasPermission = (module: NavigationModule, permission: PermissionType) => {
    const modulePermission = permissions.find(p => p.module === module);
    return modulePermission?.permissions.includes(permission) || false;
  };

  const getModuleLabel = (module: NavigationModule) => {
    return module.replace(/_/g, ' ').toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const getPermissionLabel = (permission: PermissionType) => {
    switch (permission) {
      case PermissionType.READ:
        return 'Read';
      case PermissionType.WRITE:
        return 'Write';
      case PermissionType.DELETE:
        return 'Delete';
      case PermissionType.ADMIN:
        return 'Admin';
      default:
        return permission;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-[var(--color-border)] rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-32 bg-[var(--color-border)] rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!roleData) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-[var(--color-secondary-text)]">
            Role not found
          </h2>
          <Button
            variant="outline"
            onClick={() => navigate('/rbac/roles')}
            className="mt-4"
          >
            Back to Roles
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/rbac/roles')}
            className="flex items-center space-x-2"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span>{t('common.back')}</span>
          </Button>
          <div>
            <h1 className="text-2xl font-semibold text-[var(--color-secondary-text)]">
              Edit {roleData.name}
            </h1>
            <p className="text-[var(--color-secondary-text)]">
              {roleData.description}
            </p>
          </div>
        </div>
        
        <div className="flex space-x-2">
          <Button
            variant="outline"
            onClick={() => navigate('/rbac/roles')}
            className="flex items-center space-x-2"
          >
            <XMarkIcon className="w-4 h-4" />
            <span>Cancel</span>
          </Button>
                     <Button
             onClick={handleSave}
             disabled={saving || !roleData.canBeModified}
             className="flex items-center space-x-2"
           >
             <CheckIcon className="w-4 h-4" />
             <span>{saving ? 'Saving...' : 'Save Changes'}</span>
           </Button>
        </div>
      </div>

      {/* Role Info */}
      <Card className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <h3 className="font-medium text-[var(--color-secondary-text)]">{t('rbac.roleType')}</h3>
            <Badge color={roleData.isSystemRole ? 'blue' : 'gray'} className="mt-1">
              {roleData.isSystemRole ? 'System Role' : 'Custom Role'}
            </Badge>
          </div>
          <div>
            <h3 className="font-medium text-[var(--color-secondary-text)]">Modifiable</h3>
            <Badge color={roleData.canBeModified ? 'green' : 'red'} className="mt-1">
              {roleData.canBeModified ? 'Yes' : 'No'}
            </Badge>
          </div>
          <div>
            <h3 className="font-medium text-[var(--color-secondary-text)]">Modules</h3>
            <span className="text-[var(--color-secondary-text)]">{permissions.length}</span>
          </div>
        </div>
      </Card>

      {/* Permissions */}
      <Card className="p-6">
        <h2 className="text-lg font-semibold text-[var(--color-secondary-text)] mb-4">
          Module Permissions
        </h2>
        
        {!roleData.canBeModified && (
          <div className="mb-4 p-4 bg-[var(--color-warning)]/10 border border-[var(--color-warning)]/20 rounded-md">
            <p className="text-[var(--color-warning)]">
              This role cannot be modified as it is a system role.
            </p>
          </div>
        )}

        <div className="space-y-6">
          {Object.values(NavigationModule).map((module) => (
            <div key={module} className="border border-[var(--color-border)] rounded-lg p-4">
              <h3 className="font-medium text-[var(--color-secondary-text)] mb-3">
                {getModuleLabel(module)}
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.values(PermissionType).map((permission) => (
                  <label key={permission} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasPermission(module, permission)}
                      onChange={() => togglePermission(module, permission)}
                      disabled={!roleData.canBeModified}
                      className="rounded border-[var(--color-border)] text-[var(--color-primary-bg)] focus:ring-[var(--color-primary-bg)]"
                    />
                    <span className="text-sm text-[var(--color-secondary-text)]">
                      {getPermissionLabel(permission)}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
